<script>
import { mapState, mapActions } from "vuex";
import gsap from "gsap";
import Vuetify from "vuetify";
import Grid1x1 from "@/components/grouplayouts/Grid1x1.vue";
import Grid2x2 from "@/components/grouplayouts/Grid2x2.vue";
import Grid2x6 from "@/components/grouplayouts/Grid2x6.vue";
import Grid6x6 from "@/components/grouplayouts/Grid6x6.vue";
import Grid3col from "@/components/grouplayouts/Grid3col.vue";
import Grid6 from "@/components/grouplayouts/Grid6.vue";

export default {
  name: "Group",
  props: ["id"],
  components: {
    Grid1x1,
    Grid2x2,
    Grid2x6,
    Grid6x6,
    Grid3col,
    Grid6
  },
  data() {
    return {
      groupImage: '',
      groupColorTheme: {},
      productLayout: ''
    }
  },
  watch: {
    $route (){
      this.buildGroup();
    }
  },
  created() {
    this.buildGroup()
  },
  computed: {
    ...mapState(["currentProductGroup", "maps"]),
  },
  methods: {
    ...mapActions(["SET_BREADCRUMBS"]),
    async buildGroup(){
        await this.$store.dispatch("SET_PRODUCT_GROUP", this.id);
        this.setGroupColorTheme();
        this.init();
        this.createBreadcrumb();
        this.setGroupImage();
        this.setProductLayout();
    },
    init() {
      var t1 = new gsap.timeline();
      t1.from(".title-transition > span, .title-transition > h1", {
        x: -2000,
        scale: 0.2,
        opacity: 0,
        duration: 0.9,
        ease: "expo.inOut",
      });
      t1.from(
        ".grid-transition",
        {
          x: 2000,
          duration: 0.9,
          scale: 0.2,
          opacity: 0,
          ease: "expo.inOut",
        },
        "-=.9"
      );
    },
    createBreadcrumb() {
      //Start Breadcrumbs
      const self = { name: this.currentProductGroup.name, slug: this.currentProductGroup.name_slug, route: "productgroup" };

      // Build every ancestor map path leading to `name`.
      // Returns an array of paths (immediate parent first → root last);
      // branching produces multiple paths so duplicates can be told apart.
      const buildMapPaths = (name) => {
        const paths = [];
        this.$store.state.maps.forEach((m) => {
          m.map_pins.forEach((p) => {
            var p_display = (((p.value.pin_link || {})[0] || {}).value || {})
              .name;
            if (p_display == name) {
              var crumb = { name: m.name, slug: m.name_slug, route: "map" };
              var parentPaths = buildMapPaths(m.name);
              if (parentPaths.length === 0) {
                paths.push([crumb]);
              } else {
                parentPaths.forEach((pp) => {
                  paths.push([crumb].concat(pp));
                });
              }
            }
          });
        });
        return paths;
      };

      var candidatePaths = buildMapPaths(this.currentProductGroup.name);
      var breadcrumbs = this.buildBreadcrumbs(self, candidatePaths, ["map"]);

      this.SET_BREADCRUMBS(breadcrumbs);
      //end breadcrumbs
    },
    // Assemble the final breadcrumb trail (root → current page).
    // 1. If this page is already part of the current trail (the user
    //    went back/up via the menu or the browser back button), keep the
    //    resolved path by truncating the trail here.
    // 2. If the user navigated straight from this page's parent, extend
    //    the parent's already-resolved trail — this preserves the exact
    //    path walked even when the referring slug alone can't tell
    //    duplicate paths apart.
    // 3. Otherwise pick a freshly computed candidate path by referrer.
    buildBreadcrumbs(self, candidatePaths, allowedParentRoutes) {
      var refSlug = this.referringSlug();
      var existing = this.$store.state.breadcrumbs || [];

      var selfIndex = existing.findIndex(
        (crumb) => crumb.route === self.route && crumb.slug === self.slug
      );
      if (selfIndex !== -1) {
        return existing.slice(0, selfIndex + 1);
      }

      if (refSlug && existing.length) {
        var last = existing[existing.length - 1];
        if (
          last &&
          last.slug === refSlug &&
          allowedParentRoutes.indexOf(last.route) !== -1
        ) {
          return existing.concat([self]);
        }
      }

      var path = this.selectBreadcrumbPath(candidatePaths, refSlug);
      return [self].concat(path).reverse();
    },
    // Last path segment of the URL we navigated from (e.g. the map slug),
    // used to disambiguate duplicate breadcrumb paths.
    referringSlug() {
      var referrer = (this.$router && this.$router.referrer) || "";
      if (!referrer) {
        return "";
      }
      return referrer.split("?")[0].split("/").filter(Boolean).pop() || "";
    },
    // Pick the candidate path that matches the referring URL when there
    // are duplicate paths; otherwise fall back to the first path found.
    selectBreadcrumbPath(paths, refSlug) {
      if (!paths || paths.length === 0) {
        return [];
      }
      if (paths.length === 1) {
        return paths[0];
      }
      if (refSlug) {
        var match = paths.find((p) =>
          p.some((crumb) => crumb.slug === refSlug)
        );
        if (match) {
          return match;
        }
      }
      return paths[0];
    },
        setGroupImage() {
      let background = "none";

      if (this.currentProductGroup && this.currentProductGroup.image.path) {
        background = `url( ${process.env.VUE_APP_CMS_BASE_URL +
          this.currentProductGroup.image.path})`;
      }
      this.groupImage = background;
    },
    setGroupColorTheme(){
      if( this.currentProductGroup && this.currentProductGroup.color_theme ){
        this.groupColorTheme = this.currentProductGroup.color_theme;
      } else {
        this.groupColorTheme = {};
      }
    },
    setProductLayout(){
       var p_layout = (((this.currentProductGroup.product_layout || {})[0] || {}).field || {})
              .name;
     this.productLayout = p_layout || '';
    }
  },
};
</script>

<template>
  <div
    class="productGrid"
    :style="{ borderColor: groupColorTheme.color1 }"
  >
    <div
      class="colorBand"
      :style="{
        backgroundImage: setGradient(groupColorTheme, 220),
      }"
    ></div>

    <v-row class="mainGrid" no-gutters>
      <!--START Grid Title -->
      <v-col
        class="gridTitle d-flex flex-column fill-height col-md-4 col-12"
        :style="{
          backgroundImage: groupImage,
          borderColor: this.groupColorTheme.color1,
        }"
      >
        <div
          class="holder title-transition"
          :style="{ borderColor: this.groupColorTheme.color1 }"
        >
          <span
            class="icon mr-4 mb-4"
            :style="{ backgroundColor: this.groupColorTheme.color1 }"
          >
            <v-icon
              x-large
              :style="{ color: this.groupColorTheme.textcolor }"
              >{{ currentProductGroup.icon }}</v-icon
            >
          </span>
          <h1
            class="headline d-inline"
            :style="{ color: this.groupColorTheme.color1 }"
          >
            {{ currentProductGroup.name }}
          </h1>
        </div>
      </v-col>
      <!--END Grid Title -->

      <!-- Grid Body -->
      <v-col class="gridBody col-12 col-md-8">
        <div
          class="gridBodyInner"
          :style="{ borderColor: this.groupColorTheme.color1 }"
          :data-id="this.productLayout"
        >
          <component
            :currentProductGroup="currentProductGroup"
            :is="productLayout"
          />
        </div>
      </v-col>
      <!-- END Grid Body -->
    </v-row>
  </div>
</template>

<style lang="scss">
@import "@/assets/sass/grid.scss";
</style>
